/**
 * Collapsible card explaining how a mock exam result is graded (模擬試験の結果について).
 * Shows the grade scale bar with the passing line and the A〜E criteria legend.
 * Reusable across mock exam pages (detail, result, ...).
 */
import { Box, Collapse, Divider, Stack, Typography } from "@mui/material";
import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { text, black, background } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded, spacing } from "@/theme/variables";
import { MockExamGradeBadge } from "@/features/mock-exam/components/grade-guide/MockExamGradeBadge";
import { MockExamGradeScaleBar } from "@/features/mock-exam/components/grade-guide/MockExamGradeScaleBar";
import { MOCK_EXAM_GRADES } from "@/features/mock-exam/components/grade-guide/grade-guide.constants";

type TMockExamGradeGuideCardProps = {
  /** Passing score shown on the scale bar marker, e.g. 70 → 「合格基準: 70%」 */
  passingScorePercentage: number;
  /** Whether the card starts expanded — collapsed by default. */
  defaultExpanded?: boolean;
};

export const MockExamGradeGuideCard: FC<TMockExamGradeGuideCardProps> = ({ passingScorePercentage, defaultExpanded = false }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), []);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleExpanded();
      }
    },
    [toggleExpanded],
  );

  return (
    <Box
      sx={{
        p: spacing.sm,
        borderRadius: rounded.sm,
        border: `1px solid ${black[95]}`,
        bgcolor: background.white,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={spacing.xs}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        sx={{ cursor: "pointer" }}
      >
        <Typography sx={{ ...typography.sub16Bold, color: text.primary }}>{t("mockExam.grade.guide.title")}</Typography>
        <ExpandMoreIcon
          sx={{
            flex: "0 0 auto",
            color: text.primary,
            transform: expanded ? "rotate(180deg)" : "none",
            transition: "transform 0.2s",
          }}
        />
      </Stack>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ my: spacing.smd, borderColor: black[95] }} />

        <Stack gap={spacing.smd}>
          <Typography sx={{ ...typography.body14, color: text.secondary }}>{t("mockExam.grade.guide.description")}</Typography>

          <MockExamGradeScaleBar passingScorePercentage={passingScorePercentage} />

          {/* A〜E criteria legend */}
          <Stack gap={spacing.xs}>
            {MOCK_EXAM_GRADES.map((grade) => (
              <Stack key={grade} direction="row" alignItems="center" gap={spacing.smd}>
                <MockExamGradeBadge grade={grade} />
                <Typography sx={{ ...typography.body14, color: text.primary }}>{t(`mockExam.grade.criteriaText.${grade}`)}</Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
};
